import { trackEvent } from '@vp/om-newrelic-tracker';

const ORDERS_UI_EVENT_TYPE = 'orders-ui-event';
const ORDERS_UI_ERROR_TYPE = 'orders-ui-error';
const FETCH_ANONYMOUS_ORDERS_FOR_TRANSFER_ERROR = `${ORDERS_UI_ERROR_TYPE}.FETCH_ANONYMOUS_ORDERS_FOR_TRANSFER_ERROR`;
const ANONYMOUS_ORDER_VIEWED_BY_LOGGED_IN_SHOPPER = `${ORDERS_UI_EVENT_TYPE}.ANONYMOUS_ORDER_VIEWED_BY_LOGGED_IN_SHOPPER`;
const ANONYMOUS_ORDER_TRANSFER_BANNER_PRESENTED_TO_SHOPPER = `${ORDERS_UI_EVENT_TYPE}.ANONYMOUS_ORDER_TRANSFER_BANNER_PRESENTED_TO_SHOPPER`;

// Analytics-centric custom events.
const ANALYTICS_SUBTYPE = 'orders-ui-analytics';
const ANALYTICS_TRACKING_JS_NOT_LOADED = `${ANALYTICS_SUBTYPE}.ANALYTICS_TRACKING_JS_NOT_LOADED`;
const ANALYTICS_ERROR = `${ANALYTICS_SUBTYPE}.ANALYTICS_ERROR`;

// VAT-centric custom events.
const VAT_SUBTYPE = 'orders-ui-vat-error';
const VAT_DOWNLOAD_ERROR = `${VAT_SUBTYPE}.VAT_DOWNLOAD_ERROR`;
const VAT_DOWNLOAD_ATTEMPTED = 'orders-ui-vat-download-attempted';
const VAT_DOWNLOAD_ATTEMPT_SUCCEEDED =
  'orders-ui-vat-download-attempt-succeeded';
const VAT_AVAILABILITY_FETCH_ERROR = 'orders-ui-vat-availability-fetch-error';

// Details-centric custom events.
const ORDER_DETAILS_SUBTYPE = 'orders-ui-details-error';
const TAX_TYPE_ERROR = `${ORDER_DETAILS_SUBTYPE}.UNKNOWN_TAX_TYPE_ERROR`;
const PRINT_ORDER_DETAILS = 'orders-ui-print-order-details-clicked';

// Reorder custom events
const REORDER_ATTEMPTED = 'orders-ui-reorder-attempted';
const REORDER_FAILED = 'orders-ui-reorder-attempt-failed';
const REORDER_SUCCESS = 'orders-ui-reorder-attempt-success';
const DOC_CONVERSION_MODAL_PRESENTED_TO_CUSTOMER =
  'orders-ui-legacy-doc-conversion-modal-presented';
const DOC_CONVERSION_DESIGN_TECH_REMEDIATION_ATTEMPTED =
  'orders-ui-legacy-doc-conversion-designtech-doitforyou-remediation-attempted';
const DOC_CONVERSION_DESIGN_TECH_REMEDIATION_SUCCEEDED =
  'orders-ui-legacy-doc-conversion-designtech-doitforyou-remediation-succeeded';
const DOC_CONVERSION_DESIGN_TECH_REMEDIATION_FAILED =
  'orders-ui-legacy-doc-conversion-designtech-doitforyou-remediation-failed';
const DOC_CONVERSION_DESIGN_TECH_LEGACY_MODAL_ERROR =
  'orders-ui-legacy-doc-conversion-designtech-doitforyou-error';
const DOC_CONVERSION_DESIGN_TECH_LEGACY_MODAL_UNAVAILABLE =
  'orders-ui-legacy-doc-conversion-designtech-doitforyou-unavailable';
const REORDER_UNAVAILABLE_FOR_PRODUCT =
  'orders-ui-reorder-unavailable-for-product';

// Find order custom events
const FIND_ORDER_ATTEMPTED = 'orders-ui-find-order-attempted';
const FIND_ORDER_FAILED = 'orders-ui-find-order-attempt-failed';
const FIND_ORDER_SUCCESS = 'orders-ui-find-order-attempt-success';

// Reorder similar items for retired products
const RECOMMENDATIONS_PRODUCT_ADDED_TO_CART =
  'orders-ui-recommendations-product-added-to-cart';
const RECOMMENDATIONS_UNAVAILABLE = 'orders-ui-recommendations-unavailable';
const RECOMMENDATIONS_PRESENTED_TO_SHOPPER =
  'orders-ui-recommendations-presented-to-shopper';
const VIEW_SIMILAR_ITEMS_CLICKED = 'orders-ui-view-similar-items-clicked';
const PRODUCT_LIST_WITH_VIEW_SIMILAR_ITEMS_PRESENTED =
  'orders-ui-product-list-with-view-similar-items-presented';

// Post order design edit events
const POST_ORDER_DESIGN_EDIT_ATTEMPTED =
  'orders-ui-post-order-design-edit-attempted';
const POST_ORDER_DESIGN_EDIT_REQUEST_FAILED =
  'orders-ui-post-order-design-edit-request-failed';
const POST_ORDER_DESIGN_EDIT_REQUEST_SUCCESS =
  'orders-ui-post-order-design-edit-request-success';
const POST_ORDER_DESIGN_EDIT_AVAILABILITY =
  'orders-ui-post-order-design-edit-availability';

// Buy It Again events
const BUY_IT_AGAIN_NO_REORDERABLE_ITEMS =
  'orders-ui-buy-it-again-no-reorderable-items-returned';
const BUY_IT_AGAIN_ERROR =
  'orders-ui-buy-it-again-reorderable-item-fetch-error';
const BUY_IT_AGAIN_PRESENTED = 'orders-ui-buy-it-again-presented';
const BUY_IT_AGAIN_PAGE_NUMBER_CHANGED =
  'orders-ui-buy-it-again-page-number-changed';
const ORDER_HISTORY_TAB_CHANGED = 'orders-ui-order-history-tab-changed';
const BUY_IT_AGAIN_QUANTITY_CHANGED = 'orders-ui-buy-it-again-quantity-changed';
const BUY_IT_AGAIN_CHECKOUT_CLICKED = 'orders-ui-buy-it-again-checkout-click';
// Express Checkout events
const EXPRESS_CHECKOUT_ADD_TO_CART_CLICK =
  'orders-ui-express-checkout-add-to-cart-click';
const BUY_NOW_ATTEMPTED = 'orders-ui-buy-now-attempted';
const BUY_NOW_FAILED = 'orders-ui-buy-now-attempt-failed';
const BUY_NOW_SUCCESS = 'orders-ui-buy-now-attempt-success';

// Document edit events
const DOCUMENT_EDIT_CLICKED = 'orders-ui-doc-edit-click';
const LEGACY_DOCUMENT_CONVERSION_ATTEMPTED =
  'orders-ui-legacy-document-conversion-attempted';
const LEGACY_DOCUMENT_CONVERSION_SUCCEEDED =
  'orders-ui-legacy-document-conversion-succeeded';
const LEGACY_DOCUMENT_CONVERSION_FAILED =
  'orders-ui-legacy-document-conversion-failed';

// Save VAT ID tracking
const VAT_ID_SAVE_TO_ACCOUNT_FAIL = `${ORDERS_UI_ERROR_TYPE}.VAT_ID_SAVE_TO_ACCOUNT_FAIL`;

// Payment details tracking
const PAYMENT_DETAILS_GET_FAIL = `${ORDERS_UI_ERROR_TYPE}.PAYMENT_DETAILS_GET_FAIL`;
const PRICING_CONTEXT_NOT_AVAILABLE = `PRICING_CONTEXT_API.NOT_AVAILABLE`;
const TAX_BREAKDOWN_MISSING_TAX_AMOUNT_DATA =
  'orders-ui-tax-breakdown-missing-tax-amount-data';

// General UI errors
const INVALID_DATE_STRING_PROVIDED = 'orders-ui-invalid-date-string-provided';

/**
 * Format error tracking data suitable for viewing in New Relic.
 * @param {string} reason queriable error type
 * @param {*} error error data object
 * @param {*} shopperId shopper ID of original request
 */
function createTrackingErrorData(
  reason,
  errorObj = {},
  shopperId = '',
  additionalData = {}
) {
  return {
    errorReason: reason,
    error: JSON.stringify(errorObj),
    shopper_id: shopperId,
    ...additionalData,
  };
}

/**
 * Format event tracking data suitable for viewing in New Relic.
 * Spread Operator Usage: New Relic prefers flattened structure of data, nesting JSON will make querying difficult.
 * @param {string} eventReason queriable event type
 * @param {*} shopperId shopper ID of original request
 * @param {*} eventDataObj event data object
 */
function createTrackingEventData(eventReason, shopperId, eventDataObj = {}) {
  return {
    eventReason,
    shopper_id: shopperId,
    ...eventDataObj,
  };
}

export const trackAnalyticsTrackingJsNotAvailable = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      ANALYTICS_TRACKING_JS_NOT_LOADED,
      {},
      shopperId,
      additionalData
    ),
  });
};

export const trackAnalyticsError = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      ANALYTICS_ERROR,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer attempted to download a VAT invoice
 * @param {*} additionalData
 */
export const trackVatDownloadAttempted = additionalData => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      VAT_DOWNLOAD_ATTEMPTED,
      additionalData.shopperId,
      additionalData
    ),
  });
};

/**
 * Fetch to see if VAT invoices are available failed
 * @param {*} additionalData
 */
export const trackVatInvoiceAvailableFetchError = (additionalData, err) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      VAT_AVAILABILITY_FETCH_ERROR,
      err,
      additionalData.shopperId,
      additionalData
    ),
  });
};

/**
 * VAT invoice download attempt succeeded
 * @param {*} additionalData
 */
export const trackVatDownloadAttemptSucceeded = additionalData => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      VAT_DOWNLOAD_ATTEMPT_SUCCEEDED,
      additionalData.shopperId,
      additionalData
    ),
  });
};

/**
 * VAT invoice download attempt failed
 * @param {*} additionalData
 */
export const trackVatDownloadAttemptFailed = additionalData => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      VAT_DOWNLOAD_ERROR,
      additionalData.error,
      additionalData.shopperId,
      additionalData
    ),
  });
};

/**
 * A customer failed to reorder a set of item(s)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackReorderAttemptFailed = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      REORDER_FAILED,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer failed to convert a legacy document
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentConversionAttemptFailed = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      LEGACY_DOCUMENT_CONVERSION_FAILED,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customers reorder or edit attempt of a migrated item failed, due to doc conversion failure.
 * They were presented with an option to remediate their document
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackDocumentConversionRemediationPresentedToCustomer = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      DOC_CONVERSION_MODAL_PRESENTED_TO_CUSTOMER,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer's reorder or edit attempt of a migrated item failed, due to doc conversion failure.
 * They were NOT presented with an option to get help from design services to update their document because the design service threw an error
 * @param {string} shopperId
 * @param {error}
 * @param {object} additionalData
 */
export const trackLegacyDocumentRemediationDesignServiceError = (
  shopperId = '',
  err = {},
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      DOC_CONVERSION_DESIGN_TECH_LEGACY_MODAL_ERROR,
      err,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer's reorder or edit attempt of a migrated item failed, due to doc conversion failure.
 * They were NOT presented with an option to get help from design services to update their document because the design service was unavailable
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentRemediationDesignServiceUnavailable = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      DOC_CONVERSION_DESIGN_TECH_LEGACY_MODAL_UNAVAILABLE,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer's reorder or edit attempt of a migrated item failed, due to doc conversion failure.
 * They were presented with an option to get help from design services to update their document and clicked it.
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentDesignServiceRemediationAttempted = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      DOC_CONVERSION_DESIGN_TECH_REMEDIATION_ATTEMPTED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer sent a request to Design Services to update the design of a legacy document and that request succeeded
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentDesignServiceRemediationSucceeded = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      DOC_CONVERSION_DESIGN_TECH_REMEDIATION_SUCCEEDED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer sent a request to Design Services to update the design of a legacy document and that request failed
 * @param {object} response
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentDesignServiceRemediationFailed = (
  response,
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      DOC_CONVERSION_DESIGN_TECH_REMEDIATION_FAILED,
      response,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer attempted to reorder a product which is unavailable
 * Inspect `additionalData.failureReason` to determine failure type (`REORDER_NOT_PERMITTED`, `RETIRED_PRODUCT`, etc.)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackReorderUnavailableForProduct = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      REORDER_UNAVAILABLE_FOR_PRODUCT,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer attempted to reorder a set of item(s)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackReorderAttempted = (shopperId = '', additionalData = {}) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(REORDER_ATTEMPTED, shopperId, additionalData),
  });
};

/**
 * A customer attempted to convert a legacy document
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentConversionAttempted = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      LEGACY_DOCUMENT_CONVERSION_ATTEMPTED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer successfully reordered a set of item(s)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackReorderSuccess = (shopperId = '', additionalData = {}) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(REORDER_SUCCESS, shopperId, additionalData),
  });
};

/**
 * A customer successfully converted a legacy document
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackLegacyDocumentConversionSuccess = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      LEGACY_DOCUMENT_CONVERSION_SUCCEEDED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer failed to find an order by orderNumber on the Order History page
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackFindOrderAttemptFailed = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      FIND_ORDER_FAILED,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer attempted to find an order by orderNumber on the Order History page
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackFindOrderAttempted = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(FIND_ORDER_ATTEMPTED, shopperId, additionalData),
  });
};

/**
 * A customer successfully found an order by orderNumber on the Order History page
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackFindOrderSuccess = (shopperId = '', additionalData = {}) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(FIND_ORDER_SUCCESS, shopperId, additionalData),
  });
};

/**
 * 1. On Order Details an item met our criteria (in initial, non-anonymous, non-migrated) to display the "edit design" button.
 *    Customer may have been presented with post-order design edit option depending on whether the isDocumentSupportedByPOST() call succeeded (inspect isPostSupported on metric)
 *
 * 2. On Buy It Again a reorderableItem may be supported by POST depending on whether the isDocumentSupportedByPOST() call succeeded (inspect isPostSupported on metric)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackPostOrderDesignEditAvailability = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      POST_ORDER_DESIGN_EDIT_AVAILABILITY,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer failed to request a post-order design edit
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackPostOrderDesignEditRequestFailed = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      POST_ORDER_DESIGN_EDIT_REQUEST_FAILED,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * An item has a pricing object with both null taxAmountCents and taxAmount (when generating tax breakdown)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackTaxBreakdownMissingTaxAmountData = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      TAX_BREAKDOWN_MISSING_TAX_AMOUNT_DATA,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * The call to fetch reorderable items for "Buy It Again" failed
 * @param {string} shopperId
 * @param {object} error
 * @param {object} additionalData
 */
export const trackFetchBuyItAgainItemsFail = (
  shopperId = '',
  error = {},
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingErrorData(
      BUY_IT_AGAIN_ERROR,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer has no reorderable items to display for "Buy It Again" experience
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackShopperHasNoBuyItAgainItems = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      BUY_IT_AGAIN_NO_REORDERABLE_ITEMS,
      shopperId,
      additionalData
    ),
  });
};

/**
 * The "Buy It Again" experience was presented to a customer
 * @param {string} shopperId
 * @param {object} additionalData
 * @param {number} totalPageCount
 */
export const trackBuyItAgainPresentedToCustomer = (
  shopperId = '',
  additionalData = {},
  totalPageCount = -1
) => {
  const trackingEventAdditionalData = {
    ...additionalData,
    totalPageCount,
  };
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      BUY_IT_AGAIN_PRESENTED,
      shopperId,
      trackingEventAdditionalData
    ),
  });
};

/**
 * The page on "Buy It Again" changed
 * @param {string} shopperId
 * @param {object} additionalData
 * @param {number} totalPageCount
 * @param {number} currentPageIndex
 * @param {number} selectedPageIndex
 */
export const trackBuyItAgainPageChanged = (
  shopperId = '',
  additionalData = {},
  totalPageCount = -1,
  currentPageIndex = -1,
  selectedPageIndex = -1
) => {
  const trackingEventAdditionalData = {
    ...additionalData,
    totalPageCount,
    currentPageIndex,
    selectedPageIndex,
  };
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      BUY_IT_AGAIN_PAGE_NUMBER_CHANGED,
      shopperId,
      trackingEventAdditionalData
    ),
  });
};

/**
 * A customer has clicked to switch tabs (between "Buy It Again" and traditional Order History)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackOrderHistoryExperienceTabChanged = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      ORDER_HISTORY_TAB_CHANGED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer has changed the quantity to reorder via the "Buy It Again" Experience
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackBuyItAgainQuantityChanged = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      BUY_IT_AGAIN_QUANTITY_CHANGED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer has clicked to checkout via the "Buy It Again" experience
 */
export const trackBuyItAgainCheckoutClicked = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      BUY_IT_AGAIN_CHECKOUT_CLICKED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer attempted to buy an item now (right meow)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackBuyNowAttempted = (shopperId = '', additionalData = {}) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(BUY_NOW_ATTEMPTED, shopperId, additionalData),
  });
};

/**
 * A customer successfully bought now an item
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackBuyNowSuccess = (shopperId = '', additionalData = {}) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(BUY_NOW_SUCCESS, shopperId, additionalData),
  });
};

/**
 * A customer failed to buy now an item
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackBuyNowAttemptFailed = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      BUY_NOW_FAILED,
      error,
      shopperId,
      additionalData
    ),
  });
};

/**
 * Successfully added an item to the alt cart for express checkout
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackExpressCheckoutAddToCartClick = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      EXPRESS_CHECKOUT_ADD_TO_CART_CLICK,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A shopper added a recommended product to their cart
 * Inspect additionalData to check 'recommendationType' (related or unrelated) and 'reorderScenario' (retired item, out of stock, etc.)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackRecommendedProductAddedToCart = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      RECOMMENDATIONS_PRODUCT_ADDED_TO_CART,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A shopper was eligible to be shown product recommendations but none were available (caused by an error calling the recommendations component or no results returned from the model).
 * Inspect additionalData to check 'recommendationType' (related or unrelated) and 'reorderScenario' (retired item, out of stock, etc.)
 *
 * notes:
 * - an item MpvId is required to show *related* product recommendations
 * - there are no eligibility requirements to show *unrelated* product recommendations
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackRecommendationsUnavailable = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      RECOMMENDATIONS_UNAVAILABLE,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A shopper was presented with product recommendations.
 * Inspect additionalData to check 'recommendationType' (related or unrelated) and 'reorderScenario' (retired item, out of stock, etc.)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackRecommendationsPresentedToShopper = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      RECOMMENDATIONS_PRESENTED_TO_SHOPPER,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A shopper clicked "view similar items"
 * Inspect 'reorderScenario' (retired item, out of stock, etc.)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackViewSimilarItemsClicked = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      VIEW_SIMILAR_ITEMS_CLICKED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A shopper loaded a BIA page with at least one item eligible for the "view similar items" experience
 * Inspect 'reorderScenario' (retired item, out of stock, etc.)
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackViewSimilarItemsButtonPresentedToShopper = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      PRODUCT_LIST_WITH_VIEW_SIMILAR_ITEMS_PRESENTED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer attempted to request a post-order design edit
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackPostOrderDesignEditAttempted = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      POST_ORDER_DESIGN_EDIT_ATTEMPTED,
      shopperId,
      additionalData
    ),
  });
};

/**
 * A customer successfully requested a post-order design edit
 * @param {string} shopperId
 * @param {object} additionalData
 */
export const trackPostOrderDesignEditRequestSuccess = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      POST_ORDER_DESIGN_EDIT_REQUEST_SUCCESS,
      shopperId,
      additionalData
    ),
  });
};

/**
 * Report a metric whenever an anonymously placed order is viewed by a logged in shopper.
 * @param {object} additonalData
 */
export const trackAnonymousOrderViewedByLoggedInShopper = additionalData => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      ANONYMOUS_ORDER_VIEWED_BY_LOGGED_IN_SHOPPER,
      null,
      additionalData
    ),
  });
};

/**
 * Report a metric whenever an anonymously placed order is viewed by a logged in shopper
 * with the option to transfer.
 * @param {object} additonalData
 */
export const trackAnonymousOrderTransferBannerPresentedToShopper =
  additionalData => {
    trackEvent({
      eventName: ORDERS_UI_EVENT_TYPE,
      ...createTrackingEventData(
        ANONYMOUS_ORDER_TRANSFER_BANNER_PRESENTED_TO_SHOPPER,
        null,
        additionalData
      ),
    });
  };

/**
 * Report a metric whenever a fetch of anonymous orders fails
 */
export const trackAnonymousOrdersFetchForTransferError = (
  error = {},
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      FETCH_ANONYMOUS_ORDERS_FOR_TRANSFER_ERROR,
      error,
      null,
      additionalData
    ),
  });
};

/**
 * An order has an unknown tax display mode
 */
export const trackUnknownTaxDisplayModeError = (
  error = {},
  taxDisplayMode = ''
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(TAX_TYPE_ERROR, error, null, { taxDisplayMode }),
  });
};

/**
 * A customer has clicked to print their Order Details page
 */
export const trackPrintOrderDetailsClicked = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(PRINT_ORDER_DETAILS, shopperId, additionalData),
  });
};

/**
 * A customer has clicked to edit their document
 */
export const trackDocumentEditClicked = (
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_EVENT_TYPE,
    ...createTrackingEventData(
      DOCUMENT_EDIT_CLICKED,
      shopperId,
      additionalData
    ),
  });
};

export function trackVatIdSaveToAccountError(
  error,
  shopperId,
  vatId,
  siteName
) {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(VAT_ID_SAVE_TO_ACCOUNT_FAIL, error, shopperId, {
      vatId,
      siteName,
    }),
  });
}

export function trackPaymentDetailsGetError(error, shopperId) {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(PAYMENT_DETAILS_GET_FAIL, error, shopperId),
  });
}

export function trackPricingContextNotAvailable(
  errorData,
  shopperId,
  tenantName
) {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      PRICING_CONTEXT_NOT_AVAILABLE,
      errorData,
      shopperId,
      {
        tenant: tenantName,
      }
    ),
  });
}

/**
 * An invalid prop was passed to a component expecting to consume a valid date string
 *
 * @param error
 * @param shopperId
 * @param additionalData
 */
export const trackInvalidDateStringProvided = (
  error = {},
  shopperId = '',
  additionalData = {}
) => {
  trackEvent({
    eventName: ORDERS_UI_ERROR_TYPE,
    ...createTrackingErrorData(
      INVALID_DATE_STRING_PROVIDED,
      error,
      shopperId,
      additionalData
    ),
  });
};
